import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  useMediaQuery,
  Card,
  CardContent,
  Chip,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Collapse,
  IconButton,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Error,
  Info,
  ExpandMore,
  ExpandLess,
  People,
  Business,
  CalendarToday,
  Assessment,
  Cake as CakeIcon,
} from '@mui/icons-material';

import Sidebar from '../../../../components/sidebars/Sidebar_HR';
import TopNav from '../../../../components/TopNav_hotelStaff';

import TenurePopupDialog from '../../../../components/TenurePopupDialog';
import BirthdayPopupDialog from '../../../../components/BirthdayPopupDialog';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300'];

const RetentionDashboard = () => {
  const isMobile = useMediaQuery('(max-width:768px)');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [retentionData, setRetentionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    departments: true,
    heatMap: true,
    riskAssessment: true,
    tenure: true,
    seasonal: true,
  });
  
  // State for the tenure popup dialog
  const [tenureDialogOpen, setTenureDialogOpen] = useState(false);
  const [hasShownTenurePopup, setHasShownTenurePopup] = useState(false);
  
  // State for the birthday popup dialog
  const [birthdayDialogOpen, setBirthdayDialogOpen] = useState(false);
  const [hasShownBirthdayPopup, setHasShownBirthdayPopup] = useState(false);
  const [birthdayStaffCount, setBirthdayStaffCount] = useState(0);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Close tenure dialog
  const closeTenureDialog = () => {
    setTenureDialogOpen(false);
  };
  
  // Close birthday dialog
  const closeBirthdayDialog = () => {
    setBirthdayDialogOpen(false);
  };

  // Handle view staff (optional - can navigate to staff details)
  const handleViewStaff = (staff) => {
    console.log('View staff details:', staff);
  };
  
  // Handle sending birthday wishes
  const handleSendBirthdayWishes = (staff) => {
    console.log('Birthday wishes sent to:', staff.full_name);
  };

  useEffect(() => {
    fetchRetentionData();
    checkBirthdaysToday();
  }, []);

  // Auto-show tenure popup after data loads (only once per session)
  useEffect(() => {
    if (!loading && retentionData && !hasShownTenurePopup) {
      const timer = setTimeout(() => {
        setTenureDialogOpen(true);
        setHasShownTenurePopup(true);
        sessionStorage.setItem('tenure_popup_shown', 'true');
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [loading, retentionData, hasShownTenurePopup]);
  
  // Auto-show birthday popup after tenure popup (only once per session and only if there are birthdays)
  useEffect(() => {
    if (!loading && retentionData && !hasShownBirthdayPopup && birthdayStaffCount > 0) {
      // Show birthday popup 2 seconds after tenure popup would have shown
      const timer = setTimeout(() => {
        setBirthdayDialogOpen(true);
        setHasShownBirthdayPopup(true);
        sessionStorage.setItem('birthday_popup_shown', 'true');
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [loading, retentionData, hasShownBirthdayPopup, birthdayStaffCount]);

  // Check if popups were already shown in this session
  useEffect(() => {
    const tenurePopupShown = sessionStorage.getItem('tenure_popup_shown');
    if (tenurePopupShown === 'true') {
      setHasShownTenurePopup(true);
    }
    
    const birthdayPopupShown = sessionStorage.getItem('birthday_popup_shown');
    if (birthdayPopupShown === 'true') {
      setHasShownBirthdayPopup(true);
    }
  }, []);

  const fetchRetentionData = async () => {
  try {
    setLoading(true);
    // Update this URL
    const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/hotel/HR/retention_analytics.php`);
    const result = await response.json();
    
    if (result.success) {
      setRetentionData(result.data);
    } else {
      setError(result.message || 'Failed to fetch retention data');
    }
  } catch (err) {
    console.error('Error fetching retention data:', err);
    setError('Network error occurred');
  } finally {
    setLoading(false);
  }
};

const checkBirthdaysToday = async () => {
  try {
    const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
    // Update this URL
    const response = await fetch(`${baseUrl}/hotel/HR/get_birthday_staff.php`);
    const result = await response.json();
    if (result.success && result.data && result.data.length > 0) {
      setBirthdayStaffCount(result.data.length);
    }
  } catch (err) {
    console.error('Error checking birthdays:', err);
  }
};
  const getRiskColor = (riskLevel) => {
    switch (riskLevel) {
      case 'Critical': return '#d32f2f';
      case 'High': return '#f57c00';
      case 'Medium': return '#ffb74d';
      default: return '#4caf50';
    }
  };

  const getHealthIcon = (status) => {
    switch (status) {
      case 'Excellent': return <CheckCircle sx={{ color: '#4caf50', fontSize: 40 }} />;
      case 'Good': return <TrendingUp sx={{ color: '#2196f3', fontSize: 40 }} />;
      case 'At Risk': return <Warning sx={{ color: '#ff9800', fontSize: 40 }} />;
      case 'Critical': return <Error sx={{ color: '#d32f2f', fontSize: 40 }} />;
      default: return <Info sx={{ color: '#9e9e9e', fontSize: 40 }} />;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography>Loading retention analytics...</Typography>
        </Box>
      </Box>
    );
  }

  if (error || !retentionData) {
    return (
      <Box sx={{ display: 'flex', height: '100vh' }}>
        <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
        <Box
          sx={{
            flexGrow: 1,
            p: 3,
            ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Alert severity="error">Error loading retention data: {error}</Alert>
        </Box>
      </Box>
    );
  }

  const { overall, departments, monthly_trend, tenure_analysis, risk_assessment, heat_period_analysis, summary } = retentionData;
  const turnoverData = monthly_trend.filter(m => m.monthly_turnover > 0 || m.active_employees > 0);
  const peakPeriods = heat_period_analysis?.peak_periods || [];
  const seasonalPatterns = heat_period_analysis?.seasonal_patterns || [];
  const deptHeatMap = heat_period_analysis?.department_heat_map || [];

  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />

      <Box
        sx={{
          flexGrow: 1,
          p: 3,
          ml: isMobile ? 0 : sidebarOpen ? 30 : 5,
          maxWidth: '1400px',
          mx: 'auto',
          width: '100%',
          overflow: 'auto',
          height: '100vh',
        }}
      >
        <TopNav
          onLogout={() => {
            localStorage.removeItem('authToken');
            localStorage.removeItem('userProfile');
            localStorage.removeItem('phoneNumber');
          }}
          onNotificationClick={() => alert('You have new notifications!')}
          toggleSidebar={toggleSidebar}
        />

       

        {/* Birthday Celebration Banner - Only shows if there are birthdays today */}
        {birthdayStaffCount > 0 && (
          <Alert 
            severity="warning" 
            sx={{ mb: 3, borderRadius: 2, cursor: 'pointer', background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)' }}
            onClick={() => setBirthdayDialogOpen(true)}
            icon={<CakeIcon />}
            action={
              <Button color="warning" size="small" variant="contained" sx={{ bgcolor: '#ff9800', '&:hover': { bgcolor: '#f57c00' } }}>
                View & Send Wishes 🎂
              </Button>
            }
          >
            <AlertTitle sx={{ fontWeight: 'bold' }}>
              🎉 Birthday Celebration! 🎉
            </AlertTitle>
            We have <strong>{birthdayStaffCount} staff member(s)</strong> celebrating their birthday today! 
            Click here to send them warm wishes.
          </Alert>
        )}

        {/* Info Banner about Tenured Staff */}
        <Alert 
          severity="info" 
          sx={{ mb: 3, borderRadius: 2, cursor: 'pointer' }}
          onClick={() => setTenureDialogOpen(true)}
          action={
            <Button color="inherit" size="small">
              View Details
            </Button>
          }
        >
          <AlertTitle sx={{ fontWeight: 'bold' }}>
            📊 Staff Tenure Milestone
          </AlertTitle>
          Click here to view all staff members who have been with Eleganté for 3+ months.
        </Alert>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Total Employees
                    </Typography>
                    <Typography variant="h4">{overall.total_employees}</Typography>
                  </Box>
                  <People sx={{ fontSize: 48, color: '#1976d2' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Active Employees
                    </Typography>
                    <Typography variant="h4">{overall.active_employees}</Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 48, color: '#4caf50' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Retention Rate
                    </Typography>
                    <Typography variant="h4">{overall.retention_rate}%</Typography>
                    <Typography variant="caption" color="textSecondary">
                      {summary.benchmark}
                    </Typography>
                  </Box>
                  {getHealthIcon(summary.health_status)}
                </Box>
                <LinearProgress 
                  variant="determinate" 
                  value={parseFloat(overall.retention_rate)} 
                  sx={{ mt: 1, height: 8, borderRadius: 4 }}
                  color={summary.health_status === 'Excellent' ? 'success' : summary.health_status === 'Good' ? 'primary' : 'warning'}
                />
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ borderRadius: 2, boxShadow: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" gutterBottom variant="caption">
                      Turnover Rate
                    </Typography>
                    <Typography variant="h4">{overall.turnover_rate}%</Typography>
                  </Box>
                  <TrendingDown sx={{ fontSize: 48, color: '#f57c00' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Critical Alerts */}
        {risk_assessment.filter(r => r.risk_level === 'Critical').length > 0 && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            <AlertTitle>Retention Issues Detected</AlertTitle>
            The following departments require attention: 
            {risk_assessment.filter(r => r.risk_level === 'Critical').map(r => (
              <Chip key={r.department} label={r.department} color="error" size="small" sx={{ ml: 1 }} />
            ))}
          </Alert>
        )}

        {/* Staff Distribution by Department */}
        <Grid container spacing={3} sx={{ mt: 1, mb: 3 }}>
          <Grid item xs={12}>
            <Paper
              elevation={4}
              sx={{
                p: 3,
                borderRadius: 4,
                width: '100%',
                boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
              }}
            >
              <Typography variant="h6" gutterBottom>
                Staff Distribution by Department
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart 
                  data={retentionData.active_staff_by_department?.map(dept => ({ 
                    name: dept.department, 
                    value: dept.active_staff_count 
                  })) || []}
                  layout="vertical"
                  margin={{ left: 100 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={100} />
                  <RechartsTooltip />
                  <Bar dataKey="value" fill="#1976d2" name="Active Staff Count" />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>
        </Grid>

        {/* Monthly Turnover Trend */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp /> Monthly Turnover Trend
          </Typography>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={turnoverData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" angle={-45} textAnchor="end" height={80} interval={0} />
              <YAxis yAxisId="left" />
              <YAxis yAxisId="right" orientation="right" />
              <RechartsTooltip />
              <Legend />
              <Line yAxisId="left" type="monotone" dataKey="monthly_turnover" stroke="#d32f2f" name="Turnover Count" strokeWidth={2} />
              <Line yAxisId="right" type="monotone" dataKey="retention_rate" stroke="#4caf50" name="Retention Rate %" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Paper>

        {/* Peak Periods Analysis */}
        {peakPeriods.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning /> Critical Turnover Periods
            </Typography>
            <Grid container spacing={2}>
              {peakPeriods.slice(0, 3).map((period, idx) => (
                <Grid item xs={12} md={4} key={idx}>
                  <Card sx={{ bgcolor: '#fff3e0', borderLeft: `4px solid ${period.heat_level === 'Critical' ? '#d32f2f' : '#ff9800'}` }}>
                    <CardContent>
                      <Typography variant="h6">{period.month}</Typography>
                      <Typography variant="body2">
                        Turnover: <strong>{period.turnover_count}</strong> employees
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Resignations: {period.resignations} | Terminations: {period.terminations}
                      </Typography>
                      <Chip 
                        label={period.heat_level} 
                        size="small" 
                        color={period.heat_level === 'Critical' ? 'error' : 'warning'}
                        sx={{ mt: 1 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Department Risk Assessment */}
        <Paper elevation={3} sx={{ mb: 3, borderRadius: 2 }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Assessment /> Department Risk Assessment
            </Typography>
            <IconButton onClick={() => toggleSection('riskAssessment')}>
              {expandedSections.riskAssessment ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          <Collapse in={expandedSections.riskAssessment}>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                    <TableCell>Department</TableCell>
                    <TableCell align="center">Total Staff</TableCell>
                    <TableCell align="center">Active</TableCell>
                    <TableCell align="center">Turnover Rate</TableCell>
                    <TableCell align="center">Risk Level</TableCell>
                    <TableCell>Recommendation</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {departments.map((dept) => {
                    const risk = risk_assessment.find(r => r.department === dept.department);
                    return (
                      <TableRow key={dept.department}>
                        <TableCell component="th" scope="row">
                          {dept.department}
                        </TableCell>
                        <TableCell align="center">{dept.total_employees}</TableCell>
                        <TableCell align="center">{dept.active_employees}</TableCell>
                        <TableCell align="center">
                          <Typography color={dept.turnover_rate > 20 ? 'error' : dept.turnover_rate > 10 ? 'warning' : 'textPrimary'}>
                            {dept.turnover_rate}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={risk?.risk_level || 'Low'} 
                            size="small" 
                            sx={{ bgcolor: getRiskColor(risk?.risk_level), color: 'white' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{risk?.recommendation}</Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </Paper>

        {/* Department Heat Map */}
        {deptHeatMap.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Business /> Department Turnover Heat Map (Last 12 Months)
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Department</TableCell>
                    <TableCell align="center">Month</TableCell>
                    <TableCell align="center">Turnover</TableCell>
                    <TableCell align="center">Resignations</TableCell>
                    <TableCell align="center">Terminations</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {deptHeatMap.slice(0, 10).map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.department}</TableCell>
                      <TableCell align="center">{item.month}</TableCell>
                      <TableCell align="center">
                        <Chip label={item.turnover_count} size="small" color={item.turnover_count > 2 ? 'error' : 'default'} />
                      </TableCell>
                      <TableCell align="center">{item.resignations}</TableCell>
                      <TableCell align="center">{item.terminations}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

        {/* Seasonal Patterns */}
        {seasonalPatterns.length > 0 && (
          <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarToday /> Seasonal Turnover Patterns
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={seasonalPatterns}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month_name" />
                    <YAxis />
                    <RechartsTooltip />
                    <Bar dataKey="total_turnover" fill="#ff9800" name="Turnover Count">
                      {seasonalPatterns.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.above_average ? '#d32f2f' : '#ff9800'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
              <Grid item xs={12} md={6}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Month</TableCell>
                        <TableCell align="center">Turnover</TableCell>
                        <TableCell align="center">Resignations</TableCell>
                        <TableCell align="center">Terminations</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {seasonalPatterns.map((pattern, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{pattern.month_name}</TableCell>
                          <TableCell align="center">
                            <strong>{pattern.total_turnover}</strong>
                            {pattern.above_average && <TrendingUp sx={{ fontSize: 14, ml: 0.5, color: 'error' }} />}
                          </TableCell>
                          <TableCell align="center">{pattern.resignations}</TableCell>
                          <TableCell align="center">{pattern.terminations}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          </Paper>
        )}

        {/* Tenure Analysis */}
        <Paper elevation={3} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <People /> Tenure Analysis
            </Typography>
            <Button 
              variant="contained" 
              size="small" 
              onClick={() => setTenureDialogOpen(true)}
              sx={{ bgcolor: '#1976d2' }}
            >
              View All Tenured Staff (3+ months)
            </Button>
          </Box>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={tenure_analysis}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="tenure_range" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="total_employees" fill="#1976d2" name="Total Employees" />
                  <Bar dataKey="active_employees" fill="#4caf50" name="Active Employees" />
                </BarChart>
              </ResponsiveContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Tenure Range</TableCell>
                      <TableCell align="center">Total</TableCell>
                      <TableCell align="center">Active</TableCell>
                      <TableCell align="center">Retention</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {tenure_analysis.map((tenure, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{tenure.tenure_range}</TableCell>
                        <TableCell align="center">{tenure.total_employees}</TableCell>
                        <TableCell align="center">{tenure.active_employees}</TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={`${tenure.retention_rate}%`} 
                            size="small" 
                            color={tenure.retention_rate >= 70 ? 'success' : tenure.retention_rate >= 50 ? 'warning' : 'error'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        </Paper>

        <footer style={{ textAlign: 'center', padding: '20px', marginTop: 'auto' }}>
          <Typography variant="body2" color="textSecondary">
            Designed by Elegantè IT
          </Typography>
        </footer>
      </Box>

      {/* Tenure Popup Dialog - Auto shows on page load */}
      <TenurePopupDialog 
        open={tenureDialogOpen}
        onClose={closeTenureDialog}
        minMonths={3}
        onViewStaff={handleViewStaff}
      />
      
      {/* Birthday Popup Dialog - Auto shows after tenure popup if there are birthdays */}
      <BirthdayPopupDialog 
        open={birthdayDialogOpen}
        onClose={closeBirthdayDialog}
        onSendWishes={handleSendBirthdayWishes}
      />
    </Box>
  );
};

export default RetentionDashboard;